import React, { useState, useEffect } from "react";
import {
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  CircularProgress,
  Box,
} from "@mui/material";
import { Formik, Form, Field } from "formik";
import { toast } from "react-hot-toast";
import useAccountantService from "../../api/services/AccountantService";
import * as yup from "yup";
import { getFirstTermDebtLabel } from "../../utils/utils";

// Helper functions for number formatting (allow optional minus for debt fields)
const formatNumberWithCommas = (value) => {
  if (value === null || value === undefined) return "";
  const str = value.toString().trim();
  if (str === "" || str === "-") return str;
  const isNegative = str.includes("-");
  const numericPart = str.replace(/-/g, "").replace(/[^\d.]/g, "");
  if (numericPart === "") return isNegative ? "-" : "";
  const formatted = numericPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return isNegative ? "-" + formatted : formatted;
};

const parseNumberFromFormatted = (formattedValue) => {
  if (formattedValue === null || formattedValue === undefined || formattedValue === "") return "";
  const str = formattedValue.toString().trim();
  if (str === "-") return "-0";
  const isNegative = str.includes("-");
  const withoutCommas = str.replace(/-/g, "").replace(/,/g, "");
  const num = parseFloat(withoutCommas);
  if (isNaN(num)) return "";
  return isNegative ? String(-num) : String(num);
};

/** Allowed input for debt fields: optional minus, digits, optional decimal. Keeps minus sign while typing. */
const DEBT_FIELD_PATTERN = /^-?\d*\.?\d*$/;
const isDebtField = (field) => field === "firstTermDebt" || field === "secondTermDebt";

/** Same formula as ClassStudentsAccountsData: totalFees - totalPaid */
const getCalculatedDebtBalance = (values) => {
  const toNum = (v) => {
    if (v === null || v === undefined || v === "") return 0;
    const parsed = parseNumberFromFormatted(String(v));
    if (parsed === "" || parsed === "-0") return 0;
    const n = parseFloat(parsed);
    return isNaN(n) ? 0 : n;
  };
  const totalPaid = toNum(values.firstTermPaidAmount) + toNum(values.secondTermPaidAmount);
  const totalFees =
    toNum(values.firstTermDebt) + toNum(values.secondTermDebt) +
    toNum(values.firstTermFee) + toNum(values.secondTermFee) +
    toNum(values.firstTermExamFee) + toNum(values.secondTermExamFee) +
    toNum(values.firstTermOtherContribution) + toNum(values.secondTermOtherContribution) +
    toNum(values.firstTermParentFee) + toNum(values.secondTermParentFee);
  return totalFees - totalPaid;
};

const formatBalanceDisplay = (num) =>
  new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num);

// Validation schema
const EditAccountDetailsSchema = yup.object().shape({
  firstTermDebt: yup.string().test('is-valid-amount', 'Please enter a valid amount (negative allowed)', function(value) {
    if (!value) return true;
    const numericValue = parseNumberFromFormatted(value);
    return numericValue !== "" && !isNaN(parseFloat(numericValue));
  }),
  secondTermDebt: yup.string().test('is-valid-amount', 'Please enter a valid amount (negative allowed)', function(value) {
    if (!value) return true;
    const numericValue = parseNumberFromFormatted(value);
    return numericValue !== "" && !isNaN(parseFloat(numericValue));
  }),
  firstTermFee: yup.string().test('is-valid-amount', 'Please enter a valid amount', function(value) {
    if (!value) return true;
    const numericValue = parseNumberFromFormatted(value);
    return !isNaN(numericValue) && parseFloat(numericValue) >= 0;
  }),
  secondTermFee: yup.string().test('is-valid-amount', 'Please enter a valid amount', function(value) {
    if (!value) return true;
    const numericValue = parseNumberFromFormatted(value);
    return !isNaN(numericValue) && parseFloat(numericValue) >= 0;
  }),
  firstTermExamFee: yup.string().test('is-valid-amount', 'Please enter a valid amount', function(value) {
    if (!value) return true;
    const numericValue = parseNumberFromFormatted(value);
    return !isNaN(numericValue) && parseFloat(numericValue) >= 0;
  }),
  secondTermExamFee: yup.string().test('is-valid-amount', 'Please enter a valid amount', function(value) {
    if (!value) return true;
    const numericValue = parseNumberFromFormatted(value);
    return !isNaN(numericValue) && parseFloat(numericValue) >= 0;
  }),
  firstTermOtherContribution: yup.string().test('is-valid-amount', 'Please enter a valid amount', function(value) {
    if (!value) return true;
    const numericValue = parseNumberFromFormatted(value);
    return !isNaN(numericValue) && parseFloat(numericValue) >= 0;
  }),
  secondTermOtherContribution: yup.string().test('is-valid-amount', 'Please enter a valid amount', function(value) {
    if (!value) return true;
    const numericValue = parseNumberFromFormatted(value);
    return !isNaN(numericValue) && parseFloat(numericValue) >= 0;
  }),
  firstTermParentFee: yup.string().test('is-valid-amount', 'Please enter a valid amount', function(value) {
    if (!value) return true;
    const numericValue = parseNumberFromFormatted(value);
    return !isNaN(numericValue) && parseFloat(numericValue) >= 0;
  }),
  secondTermParentFee: yup.string().test('is-valid-amount', 'Please enter a valid amount', function(value) {
    if (!value) return true;
    const numericValue = parseNumberFromFormatted(value);
    return !isNaN(numericValue) && parseFloat(numericValue) >= 0;
  }),
  firstTermPaidAmount: yup.string().test('is-valid-amount', 'Please enter a valid amount', function(value) {
    if (!value) return true;
    const numericValue = parseNumberFromFormatted(value);
    return !isNaN(numericValue) && parseFloat(numericValue) >= 0;
  }),
  secondTermPaidAmount: yup.string().test('is-valid-amount', 'Please enter a valid amount', function(value) {
    if (!value) return true;
    const numericValue = parseNumberFromFormatted(value);
    return !isNaN(numericValue) && parseFloat(numericValue) >= 0;
  }),
});

const getInitialValues = (accountantData) => {
  if (!accountantData) {
    return {
      firstTermDebt: "",
      secondTermDebt: "",
      firstTermFee: "",
      secondTermFee: "",
      firstTermExamFee: "",
      secondTermExamFee: "",
      firstTermOtherContribution: "",
      secondTermOtherContribution: "",
      firstTermParentFee: "",
      secondTermParentFee: "",
      firstTermPaidAmount: "",
      secondTermPaidAmount: "",
    };
  }

  return {
    firstTermDebt: accountantData.firstTermDebt !== null && accountantData.firstTermDebt !== undefined ? String(Number(accountantData.firstTermDebt)) : "",
    secondTermDebt: accountantData.secondTermDebt !== null && accountantData.secondTermDebt !== undefined ? String(Number(accountantData.secondTermDebt)) : "",
    firstTermFee: accountantData.firstTermFee ? formatNumberWithCommas(accountantData.firstTermFee) : "",
    secondTermFee: accountantData.secondTermFee ? formatNumberWithCommas(accountantData.secondTermFee) : "",
    firstTermExamFee: accountantData.firstTermExamFee ? formatNumberWithCommas(accountantData.firstTermExamFee) : "",
    secondTermExamFee: accountantData.secondTermExamFee ? formatNumberWithCommas(accountantData.secondTermExamFee) : "",
    firstTermOtherContribution: accountantData.firstTermOtherContribution ? formatNumberWithCommas(accountantData.firstTermOtherContribution) : "",
    secondTermOtherContribution: accountantData.secondTermOtherContribution ? formatNumberWithCommas(accountantData.secondTermOtherContribution) : "",
    firstTermParentFee: accountantData.firstTermParentFee ? formatNumberWithCommas(accountantData.firstTermParentFee) : "",
    secondTermParentFee: accountantData.secondTermParentFee ? formatNumberWithCommas(accountantData.secondTermParentFee) : "",
    firstTermPaidAmount: accountantData.firstTermPaidAmount ? formatNumberWithCommas(accountantData.firstTermPaidAmount) : "",
    secondTermPaidAmount: accountantData.secondTermPaidAmount ? formatNumberWithCommas(accountantData.secondTermPaidAmount) : "",
  };
};

const EditAccountDetails = ({ open, onClose, onSuccess, accountantData, classTitle }) => {
  const { updatePartialAccountantDataByTermStudent } = useAccountantService();
  const [isLoading, setIsLoading] = useState(false);
  const [initialValues, setInitialValues] = useState(getInitialValues(null));

  // Load data when dialog opens or accountantData changes
  useEffect(() => {
    if (open && accountantData) {
      setInitialValues(getInitialValues(accountantData));
    }
  }, [open, accountantData]);

  const handleSubmit = async (values, { setSubmitting }) => {
    if (!accountantData || !accountantData.termId || !accountantData.studentId) {
      toast.error("Missing required information. Cannot update account details.");
      setSubmitting(false);
      return;
    }

    try {
      setIsLoading(true);
      const dto = {};
      
      // Non-debt fields: empty -> 0. Debt fields: empty -> 0, otherwise allow negative.
      const parseOrZero = (value) => {
        const parsed = parseNumberFromFormatted(value);
        if (parsed === "" || parsed === null || parsed === undefined) return 0;
        const numValue = parseFloat(parsed);
        return isNaN(numValue) ? 0 : numValue;
      };

      const firstDebt = values.firstTermDebt !== "" && values.firstTermDebt != null ? parseNumberFromFormatted(values.firstTermDebt) : "";
      const secondDebt = values.secondTermDebt !== "" && values.secondTermDebt != null ? parseNumberFromFormatted(values.secondTermDebt) : "";
      dto.firstTermDebt = firstDebt !== "" && firstDebt !== "-0" ? parseFloat(firstDebt) : parseOrZero(values.firstTermDebt);
      dto.secondTermDebt = secondDebt !== "" && secondDebt !== "-0" ? parseFloat(secondDebt) : parseOrZero(values.secondTermDebt);
      dto.firstTermFee = parseOrZero(values.firstTermFee);
      dto.secondTermFee = parseOrZero(values.secondTermFee);
      dto.firstTermExamFee = parseOrZero(values.firstTermExamFee);
      dto.secondTermExamFee = parseOrZero(values.secondTermExamFee);
      dto.firstTermOtherContribution = parseOrZero(values.firstTermOtherContribution);
      dto.secondTermOtherContribution = parseOrZero(values.secondTermOtherContribution);
      dto.firstTermParentFee = parseOrZero(values.firstTermParentFee);
      dto.secondTermParentFee = parseOrZero(values.secondTermParentFee);
      dto.firstTermPaidAmount = parseOrZero(values.firstTermPaidAmount);
      dto.secondTermPaidAmount = parseOrZero(values.secondTermPaidAmount);

      // Update the accountant record
      await updatePartialAccountantDataByTermStudent(
        accountantData.termId,
        accountantData.studentId,
        dto,
        () => {
          onSuccess?.();
          onClose();
        }
      );
    } catch (error) {
      console.error("Error updating account details:", error);
      toast.error(error.response?.data?.message || "Failed to update account details");
    } finally {
      setIsLoading(false);
      setSubmitting(false);
    }
  };

  if (!accountantData) {
    return null;
  }

  const termName = accountantData.term?.termName || accountantData.termName || "N/A";
  const studentName = accountantData.student?.studentName || accountantData.studentName || "N/A";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        EDIT ACCOUNT DETAILS
        <div style={{ fontSize: '14px', fontWeight: 'normal', marginTop: '8px', color: '#666' }}>
          Term: <strong>{termName}</strong> | Student: <strong>{studentName}</strong>
        </div>
      </DialogTitle>
      <DialogContent>
        {isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress color="secondary" />
          </Box>
        ) : (
          <Formik
            initialValues={initialValues}
            validationSchema={EditAccountDetailsSchema}
            onSubmit={handleSubmit}
            enableReinitialize={true}
          >
            {({ values, errors, touched, setFieldValue, isSubmitting }) => (
              <Form>
                <Grid container spacing={2} mt={1}>
                  {/* Term (half) and Debt Balance (half, read-only) */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Term"
                      variant="filled"
                      value={termName}
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Debt Balance"
                      variant="filled"
                      value={formatBalanceDisplay(getCalculatedDebtBalance(values))}
                      InputProps={{ readOnly: true }}
                      helperText="Calculated automatically (Total fees − Total paid)"
                    />
                  </Grid>
                  {/* Financial Fields */}
                  {[
                    { field: "firstTermDebt", label: getFirstTermDebtLabel(classTitle || accountantData?.classTitle) },
                    { field: "secondTermDebt", label: "Second Term Debt" },
                    { field: "firstTermFee", label: "First Term Fee" },
                    { field: "secondTermFee", label: "Second Term Fee" },
                    { field: "firstTermExamFee", label: "First Term Exam Fee" },
                    { field: "secondTermExamFee", label: "Second Term Exam Fee" },
                    { field: "firstTermOtherContribution", label: "First Term Other Contribution" },
                    { field: "secondTermOtherContribution", label: "Second Term Other Contribution" },
                    { field: "firstTermParentFee", label: "First Term Parent Fee" },
                    { field: "secondTermParentFee", label: "Second Term Parent Fee" },
                    { field: "firstTermPaidAmount", label: "First Term Paid Amount" },
                    { field: "secondTermPaidAmount", label: "Second Term Paid Amount" },
                  ].map(({ field, label }) => {
                    const isDebt = isDebtField(field);
                    return (
                      <Grid item xs={12} lg={6} key={field}>
                        <TextField
                          fullWidth
                          label={label}
                          variant="filled"
                          value={isDebt ? (values[field] ?? "") : formatNumberWithCommas(values[field])}
                          onChange={(e) => {
                            const input = e.target.value;
                            if (isDebt) {
                              if (DEBT_FIELD_PATTERN.test(input)) setFieldValue(field, input);
                            } else {
                              const formattedValue = formatNumberWithCommas(input);
                              setFieldValue(field, formattedValue);
                            }
                          }}
                          error={touched[field] && !!errors[field]}
                          helperText={touched[field] && errors[field]}
                          placeholder={isDebt ? "e.g. -300000" : "0"}
                        />
                      </Grid>
                    );
                  })}
                </Grid>

                <DialogActions sx={{ mt: 2 }}>
                  <Button onClick={onClose} color="secondary" disabled={isSubmitting || isLoading}>
                    Cancel
                  </Button>
                  <Button type="submit" variant="contained" color="secondary" disabled={isSubmitting || isLoading}>
                    {isSubmitting || isLoading ? "Updating..." : "Update"}
                  </Button>
                </DialogActions>
              </Form>
            )}
          </Formik>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EditAccountDetails;

